import { array, boolean, number, object, string } from "yup";
// @ts-expect-error pure js
import { dateMax, integerOrDefault, stringMax } from "./utils";
import { AutreAppelCategorie, ChargesType } from "../utils/constants/tenants";
import type { AutreAppel } from "./autre_appel";

export class PropertyStatus {
  static Actif = new PropertyStatus(0);
  static Archive = new PropertyStatus(1);

  status: number;

  constructor(status: number) {
    this.status = status;
  }
}

export class PropertyType {
  static BIEN_COMPTABLE = "bien_comptable";
  static BIEN_NON_COMPTABLE = "bien_non_comptable";
}

export const DETAIL_CHARGES = [
  "Eau froide",
  "Eau chaude",
  "Chauffage collectif",
  "Charges de copropriété",
  "Taxe d'ordures ménagères",
];

export const DETAIL_CHARGES_PRO = ["Taxe foncière"];

export const propertyHeadSchema = object({
  nom: stringMax(250).required("Le nom est obligatoire"),
  id_profile_nom_owner: number().required(
    "Vous devez selectionner un profil bailleur",
  ),
  rue: stringMax(250).required("L'adresse est obligatoire"),
  complement_adresse: stringMax(37).optional(),
  ville: stringMax(250).required("La ville est obligatoire"),
  cp: stringMax(10).required("Le code postal est obligatoire"),
  status: number().optional(),
});

export const propertyEquipementsLocalSchema = object({
  chauffage: stringMax(250).optional(),
  eau_chaude: stringMax(250).optional(),
  iban: stringMax(250).optional(),
  iban_titulaire: stringMax(250).optional(),
  surface_total: number().when(["type", "is_immeuble"], {
    is: (type: string, is_immeuble: boolean) =>
      type === "habitation" && !is_immeuble,
    then: number().required("La surface est obligatoire"),
    otherwise: number().optional(),
  }),
  nb_pieces_principales: number().when(["type", "is_immeuble"], {
    is: (type: string, is_immeuble: boolean) =>
      type === "habitation" && !is_immeuble,
    then: number().required(
      "Le nombre de pièces principales du logement est obligatoire",
    ),
    otherwise: number().optional(),
  }),
  autres_pieces: array().of(string()),
  equipements: array().of(string()),
  technologies: array().of(string()),
  locaux_privatifs: array().of(string()),
  locaux_communs: array().of(string()),
  logement_espaces_privatifs: array().of(string()),
  logement_espaces_communs: array().of(string()),
  depenses_energetiques_min: number().optional(),
  depenses_energetiques_max: number().optional(),
  type_propriete: stringMax(250).optional(),
  type_habitat: stringMax(250).optional(),
  year_built: string()
    .matches(
      /^(\d{4}|avant \d{4}|entre \d{4} et \d{4}|après \d{4})?$/,
      "Indiquez l'année sur 4 chiffres (ex. 1972)",
    )
    .optional(),
  date_achat: dateMax().optional(),
  description: string().when(["type"], {
    is: (type: string) => type === "commercial",
    then: string().required("Une description détaillée est obligatoire"),
    otherwise: string().optional(),
  }),
  description_public: string().optional(),
  article_specific: string().optional(),
  autres_remarques: stringMax(250).optional(),
  identifiant_fiscal: stringMax(250).optional(),
  compteur_gaz: stringMax(250).optional(),
  compteur_elec: stringMax(250).optional(),
  compteur_eau: stringMax(250).optional(),
  indice_dpe: stringMax(250).optional(),
  not_applicable_diagnostics: array().of(string()),
  prix_achat: number()
    .integer("Veuillez spécifier un nombre entier")
    .optional(),
  clauses_specifiques: array().of(string()),
  gli: boolean().optional(),
});
export const propertyFinanceSchema = object({
  type_charge: stringMax(250).optional(),
  iban: stringMax(250).optional(),
  iban_titulaire: stringMax(250).optional(),
  id_lease_template: number().optional(),
  avantage_fiscal: stringMax(250).optional(),
  loyer_hc: number().when("is_immeuble", {
    is: true,
    then: number().optional(),
    otherwise: number().required(
      "Le montant du loyer hors charge est obligatoire",
    ),
  }),
  charges: number().when("is_immeuble", {
    is: true,
    then: number().optional(),
    otherwise: number().required("Le montant des charges est obligatoire"),
  }),
  deposit: number().when("is_immeuble", {
    is: true,
    then: number().optional(),
    otherwise: number().required(
      "Le montant du dépôt de garantie est obligatoire",
    ),
  }),
  day_payment: integerOrDefault(5),
  zone_tendue: string(),
  complement_loyer: number().optional(),
  justif_complement_loyer: string().optional(),
  dernier_loyer: number().optional(),
  loyer_reference: stringMax(250),
  montant_loyer_ref: number().when(["loyer_reference"], {
    is: (loyer_reference: string) => loyer_reference === "true",
    then: number().required("Le loyer de référence est obligatoire"),
    otherwise: number().optional(),
  }),
  montant_loyer_ref_maj: number().when(["loyer_reference"], {
    is: (loyer_reference: string) => loyer_reference === "true",
    then: number().required("Le loyer de référence majoré est obligatoire"),
    otherwise: number().optional(),
  }),
  tva: boolean().optional(),
  taux_tva: number().optional(),
  tva_charge: boolean().optional(),
  taux_tva_charge: number().optional(),
  type_indice: stringMax(250).optional(),
  detail_charges: array().of(string()).optional(),
  nb_rooms: number().optional(),
  autre_appel: array()
    .of(
      object({
        categorie: string()
          .oneOf(Object.values(AutreAppelCategorie))
          .required("La catégorie est obligatoire"),
        label: string().when("categorie", {
          is: "autre_charge",
          then: () => stringMax(250).required("La description est obligatoire"),
          otherwise: (schema) => schema.notRequired(),
        }),
        montant: number().required("Le montant est obligatoire"),
        tva: boolean(),
        remboursement: boolean(),
      }),
    )
    .optional(),
});
export const propertyDocumentSchema = object({
  indice_dpe: stringMax(250).optional(),
  pollution: stringMax(250),
  date_dpe: dateMax().optional(),
  date_plomb: dateMax().optional(),
  date_amiante: dateMax().optional(),
  date_pollution: dateMax().optional(),
  date_elec: dateMax().optional(),
  date_gaz: dateMax().optional(),
});
export const appartementSchema = object({
  nom: string().optional(),
  id_lease_template: number().required("Le type de bail est obligatoire"),
  nb_pieces_principales: number().required(
    "Le nombre de pièces est obligatoire",
  ),
  surface_total: number().required("La surface est obligatoire"),
  loyer_hc: number().required("Le loyer est obligatoire"),
  charges: number().required("Les charges sont obligatoires"),
  deposit: number().required("Le dépôt de garantie est obligatoire"),
});

export const propertySchema = propertyEquipementsLocalSchema
  .concat(propertyHeadSchema)
  .concat(propertyFinanceSchema)
  .concat(propertyDocumentSchema)
  .concat(
    object({
      type: stringMax(250).required("Le type de bien est obligatoire"),
      id_parent: number().optional(),
      photos: string().optional(),
      is_coloc: string().optional(),
      is_immeuble: boolean().optional(),
      appartements: array()
        .of(appartementSchema)
        .when("is_immeuble", {
          is: true,
          then: array().of(appartementSchema).optional(),
          otherwise: array().optional(),
        }),
    }),
  );
export const folderSchema = object({
  type: stringMax(250).required("Le type de bien est obligatoire"),
  nom: stringMax(250).required("Un nom de dossier est obligatoire"),
  date_achat: dateMax().optional(),
  prix_achat: number()
    .integer("Veuillez spécifier un nombre entier")
    .optional(),
  id_profile_nom_owner: number().required(
    "Vous devez selectionner un profil bailleur",
  ),
});

export const PROPERTY_INITIAL_VALUES = {
  nom: "",
  id_profile_nom_owner: "",
  rue: "",
  complement_adresse: "",
  ville: "",
  cp: "",
  date_achat: "",
  type: "",
  type_charge: ChargesType.PROVISION,
  id_parent: "",
  description: "",
  description_public: "",
  photos: "",
  loyer_hc: "",
  charges: "",
  deposit: "",
  surface_total: "",
  id_lease_template: "",
  year_built: "",
  type_propriete: "copropriete",
  avantage_fiscal: "",
  type_habitat: "",
  chauffage: "individuel",
  eau_chaude: "individuel",
  locaux_privatifs: [],
  locaux_communs: [],
  logement_espaces_privatifs: [],
  logement_espaces_communs: [],
  technologies: [],
  autres_remarques: "",
  depenses_energetiques_min: "",
  depenses_energetiques_max: "",
  autres_pieces: [],
  equipements: [],
  nb_coloc: "",
  day_payment: "5",
  nb_pieces_principales: "",
  prix_achat: "",
  indice_dpe: "",
  not_applicable_diagnostics: [],
  pollution: "",
  article_specific: "",
  date_dpe: "",
  date_plomb: "",
  date_amiante: "",
  date_pollution: "",
  date_elec: "",
  date_gaz: "",
  status: PropertyStatus.Actif.status.toString(),
  is_coloc: false,
  is_immeuble: false,
  zone_tendue: "false",
  complement_loyer: "",
  justif_complement_loyer: "",
  dernier_loyer: "",
  loyer_reference: "false",
  montant_loyer_ref: "",
  montant_loyer_ref_maj: "",
  iban: "",
  iban_titulaire: "",
  identifiant_fiscal: "",
  files: [],
  tva: false,
  taux_tva: "",
  tva_charge: false,
  taux_tva_charge: "",
  type_indice: "",
  detail_charges: [],
  compteur_gaz: "",
  compteur_elec: "",
  compteur_eau: "",
  nb_rooms: 0,
  autre_appel: [],
  clauses_specifiques: [],
  appartements: [],
  gli: false,
};

export const PROPERTY_FOLDER_INITIAL_VALUES = {
  nom: "",
  id_profile_nom_owner: "",
  prix_achat: "",
  date_achat: "",
  surface_total: "",
};

export const PROPERTY_FISCALITE_FORM_IV = {
  rue: "",
  complement_adresse: "",
  ville: "",
  cp: "",
  prix_achat: "",
  date_achat: "",
};

export const propertyFiscaliteSchema = object({
  rue: stringMax(250),
  complement_adresse: stringMax(37),
  ville: stringMax(250),
  cp: stringMax(10),
  date_achat: dateMax().optional(),
  prix_achat: number()
    .integer("Veuillez spécifier un nombre entier")
    .optional(),
});

export interface Property {
  id: number;
  nom: string;
  type: string;
  status: number;
  abonnement_actif: boolean;
  /** Tags de diagnostics dont le bien est dispensé (« Non concerné » dans le pilotage). */
  not_applicable_diagnostics?: string[];
  /** Comptes de produit du bien dans la comptabilité du bailleur (export FEC) */
  rent_revenue_account?: string | null;
  charges_revenue_account?: string | null;

  rue: string;
  cp: string;
  ville: string;

  id_profile_nom_owner: number;
  is_coloc: boolean;

  loyer_hc: number;
  charges: number;
  deposit: number;
  day_payment: number;
  type_charge: string;
  type_propriete: string;

  tva: boolean;
  tva_charge: boolean;

  id_lease_template: number;
  autre_appel: AutreAppel[];

  autres_pieces: string[];
  locaux_communs: string[];
  locaux_privatifs: string[];
  logement_espaces_communs: string[];
  logement_espaces_privatifs: string[];
  technologies: string[];
  equipements: string[];

  nom_bien?: string;
  main_photo_id?: number;
  id_property_folder?: number;

  adresse?: string;
  complement_adresse?: string;
  zone_tendue?: boolean;
  /** Zone A à D du plan d'exposition au bruit d'un aérodrome couvrant l'adresse, null hors zone. */
  noise_exposure_zone?: string | null;
  /** Date de la vérification de la zone de bruit par le back ; null tant que l'adresse n'a pas été vérifiée. */
  noise_exposure_checked_at?: string | null;

  type_indice?: string | null;

  taux_tva?: number;
  taux_tva_charge?: number;

  payment_recurrence?: number;
  loyer_reference?: boolean;
  montant_loyer_ref?: number;
  montant_loyer_ref_maj?: number;
  complement_loyer?: number;

  surface_total?: number;
  nb_pieces_principales?: number;
  date_achat?: string;
  type_habitat?: string;
  year_built?: string;

  eau_chaude?: string;
  chauffage?: string;

  compteur_eau?: string;
  compteur_elec?: string;
  compteur_gaz?: string;
  compteur_fibre?: string;
  indice_dpe?: string;
  depenses_energetiques_min?: number;
  depenses_energetiques_max?: number;
  iban?: string;
  iban_titulaire?: string;
  description?: string;
  description_public?: string;
  gli?: boolean;

  cles?: { nombre: number | string; description?: string }[];
  photos_cles?: { id?: number; src: string; mimetype?: string }[];
}

// Le back stocke la région IRL (déduite du code postal) dans type_indice pour
// les biens indexés sur l'IRL ; côté UI on affiche/sélectionne juste "IRL"
export const IRL_REGIONS = ["FR", "CORSE", "OUTRE-MER"];

/** Indices de révision proposés à la saisie, avec leur libellé long. */
export const INDEX_TYPE_LABELS: Record<string, string> = {
  IRL: "Indice de référence des loyers (habitation)",
  ILC: "Indice des loyers commerciaux",
  ILAT: "Indice des loyers des activités tertiaires",
  ICC: "Indice du coût de la construction",
};
export const INDEX_TYPES = Object.keys(INDEX_TYPE_LABELS);

export function formatTypeIndice(typeIndice?: string | null) {
  if (!typeIndice) {
    return "Aucun";
  }
  return IRL_REGIONS.includes(typeIndice) ? "IRL" : typeIndice;
}

/** Catégorie du décret n° 2013-392 : 1° agglomération > 50 000 habitants, 2° commune touristique en tension. */
export type ZoneTendueCategorie = "agglomeration" | "touristique";

/** Réponse de GET /biens/zone-tendue (cf. `ZoneTendueLookup` côté back). */
export interface ZoneTendueLookup {
  code_insee: string;
  zone_tendue: boolean;
  categorie: ZoneTendueCategorie | null;
}
